import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Group, Stack, Text } from '@mantine/core';
import { FileText, Lock, X } from 'react-feather';
import {
  RichTextEditor,
  LINK_CLICKABILITY_HELPER_TEXT,
  type RichTextControl,
} from '../../../../components/common/RichTextEditor/RichTextEditor';
import { participantActiveSessionApi } from '../../../../services/api/participantActiveSession';

/**
 * Right-rail slide-in notes panel for the participant active session screen
 * (MED-805 AC #7-#10, IV3).
 *
 * Behavior contract:
 *   - WYSIWYG editor (Tiptap, bold/italic at minimum — RichTextEditor exposes
 *     more options which is fine).
 *   - Initial content hydrated from `GET /notes` on mount.
 *   - Edits debounce-autosave to `PUT /notes` after `AUTOSAVE_DEBOUNCE_MS`.
 *     MED-1030 AC6 — a green-dot "Saved" indicator sits in the toolbar row
 *     (no timestamp) once the notes have persisted at least once.
 *   - The panel can be closed via the × in the header — the pencil FAB stays
 *     visible at all times so the participant can re-open (AC #10).
 *   - Privacy is enforced server-side (IV3) — this panel makes no other
 *     reveal/share affordances.
 */

const AUTOSAVE_DEBOUNCE_MS = 600;

/**
 * MED-1165 — single source of truth for the drawer width. The page reserves
 * this same width on the right of the content region while the drawer is open
 * (push layout) so the reserved inset and the drawer can't drift apart.
 */
export const PANEL_WIDTH = 360;

// MED-1030 AC6 — reduced toolbar matching the coach Session Notes (MED-1014):
// B I U | bullet · ordered | link. No strikethrough / headings.
const NOTES_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline'],
  ['bulletList', 'orderedList'],
  ['link'],
];

export interface NotesPanelProps {
  sessionId: string;
  open: boolean;
  onClose: () => void;
  /**
   * MED-1030 — DEV-only static preview (`/dev/participant-session`). The fixture
   * supplies a mock `sessionId`, so skip the hydrate fetch + autosave PUT (they'd
   * 401 and bounce the page to /login) and just exercise the editor UI.
   */
  preview?: boolean;
}

export function NotesPanel({
  sessionId,
  open,
  onClose,
  preview = false,
}: NotesPanelProps) {
  const [notesHtml, setNotesHtml] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<number | null>(null);
  const lastSavedHtmlRef = useRef('');

  // Hydrate once on mount, regardless of whether the panel is open — the
  // participant can open it at any time and the editor should already know
  // the latest notes.
  useEffect(() => {
    // DEV preview — no backend; show the editor immediately, skip the fetch.
    if (preview) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const result = await participantActiveSessionApi.getNotes(sessionId);
        if (cancelled) return;
        setNotesHtml(result.notes_html ?? '');
        lastSavedHtmlRef.current = result.notes_html ?? '';
        setSavedAt(result.updated_at ? new Date(result.updated_at) : null);
      } catch {
        // Non-blocking: if the load fails, the editor still works and the
        // first edit will create a row.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, preview]);

  const persist = (html: string) => {
    if (preview) {
      // DEV harness — no backend; flag "Saved" without a network round-trip.
      setSavedAt(new Date());
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await participantActiveSessionApi.putNotes(
            sessionId,
            html
          );
          lastSavedHtmlRef.current = result.notes_html ?? '';
          setSavedAt(
            result.updated_at ? new Date(result.updated_at) : new Date()
          );
        } catch {
          // Swallow — autosave will retry on the next keystroke.
        }
      })();
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  // Flush the debounce timer on unmount so a final keystroke isn't lost.
  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  // MED-1030 AC6 — green-dot "Saved" indicator for the toolbar row (no
  // timestamp). Shown once the notes have persisted at least once.
  const savedIndicator = savedAt ? (
    <Group
      gap={6}
      align="center"
      wrap="nowrap"
      data-testid="participant-active-notes-saved-indicator"
    >
      <Box
        w={8}
        h={8}
        style={{
          borderRadius: '50%',
          backgroundColor: 'var(--mantine-color-medleyGreen-6)',
        }}
      />
      <Text size="xs" c="dimmed">
        Saved
      </Text>
    </Group>
  ) : null;

  return (
    <Box
      data-testid="participant-active-notes-panel"
      data-open={open ? 'true' : 'false'}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        backgroundColor: '#FFFFFF',
        // Reset the inherited white color from the immersive dark page wrapper
        // so the header title + icon read against the white panel background.
        color: '#1A1F36',
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.2)',
        transform: open ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
        transition: 'transform 220ms ease-out',
        zIndex: 11,
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
      }}
    >
      <Group justify="space-between" align="flex-start" mb="md">
        <Group gap={10} align="flex-start" wrap="nowrap">
          <FileText
            size={18}
            aria-hidden="true"
            style={{ position: 'relative', top: 2, color: '#5A6478' }}
          />
          <Stack gap={2}>
            <Text fw={600} size="sm">
              My Notes
            </Text>
            <Group gap={4} align="center" wrap="nowrap">
              <Lock size={12} aria-hidden="true" color="#8A93A6" />
              <Text size="xs" c="dimmed">
                Private to you
              </Text>
            </Group>
          </Stack>
        </Group>
        <ActionIcon
          variant="subtle"
          size="md"
          aria-label="Close notes panel"
          data-testid="participant-active-notes-close"
          onClick={onClose}
        >
          <X size={16} />
        </ActionIcon>
      </Group>

      <Box
        style={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // MED-1030 — bleed the editor to the drawer's full width (cancel the
          // panel's 16px side padding); the editor's own toolbar/content
          // padding keeps the text off the edges. Header stays padded.
          marginInline: -16,
        }}
      >
        {hydrated ? (
          <RichTextEditor
            value={notesHtml}
            onChange={html => {
              setNotesHtml(html);
              if (html !== lastSavedHtmlRef.current) persist(html);
            }}
            placeholder="Take your time. This is just for you."
            fillHeight
            seamless
            toolbar={NOTES_TOOLBAR}
            toolbarRight={savedIndicator}
            helperText={LINK_CLICKABILITY_HELPER_TEXT}
          />
        ) : (
          <Text size="sm" c="dimmed" ta="center" mt="lg">
            Loading…
          </Text>
        )}
      </Box>
    </Box>
  );
}
